import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { firmAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface FirmDisposition {
  id: number;
  name: string;
  is_active: boolean;
  firm_id: number | null;
}

const ClientFirmSettings: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    category: '' as 'prepaid' | 'postpaid' | 'trial' | 'suspended' | '',
    currency: 'USD',
  });
  const [dispositions, setDispositions] = useState<FirmDisposition[]>([]);
  const [dispositionsLoading, setDispositionsLoading] = useState(false);
  const [dispositionDialogOpen, setDispositionDialogOpen] = useState(false);
  const [dispositionEditId, setDispositionEditId] = useState<number | null>(null);
  const [dispositionName, setDispositionName] = useState('');
  const [dispositionIsActive, setDispositionIsActive] = useState(true);
  const [dispositionSaving, setDispositionSaving] = useState(false);

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const currencies = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'CNY'];

  useEffect(() => {
    if (!isPmpAdminOrSiteAdmin()) {
      setError('Only PMP admins or Site admins can access firm settings');
      return;
    }
    fetchSettings();
    fetchDispositions();
  }, [firmId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await firmAPI.getSettings(parseInt(firmId!));
      if (response.data.success) {
        setSettings({
          category: response.data.data.category || '',
          currency: response.data.data.currency || 'USD',
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDispositions = async () => {
    try {
      setDispositionsLoading(true);
      const response = await firmAPI.getDispositions(parseInt(firmId!));
      if (response.data.success) {
        setDispositions(response.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch dispositions');
    } finally {
      setDispositionsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await firmAPI.updateSettings(parseInt(firmId!), settings);
      if (response.data.success) {
        setSuccess('Settings updated successfully');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const openAddDisposition = () => {
    setDispositionEditId(null);
    setDispositionName('');
    setDispositionIsActive(true);
    setDispositionDialogOpen(true);
  };

  const openEditDisposition = (d: FirmDisposition) => {
    setDispositionEditId(d.id);
    setDispositionName(d.name);
    setDispositionIsActive(d.is_active);
    setDispositionDialogOpen(true);
  };

  const handleSaveDisposition = async () => {
    const name = dispositionName.trim();
    if (!name) {
      setError('Disposition name is required');
      return;
    }
    try {
      setDispositionSaving(true);
      setError('');
      if (dispositionEditId != null) {
        await firmAPI.updateDisposition(parseInt(firmId!), dispositionEditId, {
          name,
          is_active: dispositionIsActive,
        });
        setSuccess('Disposition updated');
      } else {
        await firmAPI.createDisposition(parseInt(firmId!), {
          name,
          is_active: dispositionIsActive,
        });
        setSuccess('Disposition added');
      }
      setDispositionDialogOpen(false);
      await fetchDispositions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save disposition');
    } finally {
      setDispositionSaving(false);
    }
  };

  const handleDeleteDisposition = async (id: number) => {
    if (!window.confirm('Deactivate this disposition? It will no longer appear in timer dropdowns for this client.')) {
      return;
    }
    try {
      setError('');
      await firmAPI.deleteDisposition(parseInt(firmId!), id);
      setSuccess('Disposition deactivated');
      await fetchDispositions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate disposition');
    }
  };

  if (!isPmpAdminOrSiteAdmin()) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Only PMP admins or Site admins can access firm settings</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="#" onClick={() => navigate('/firms')}>
          Firms
        </Link>
        <Link color="inherit" href="#" onClick={() => navigate(`/firms/${firmId}`)}>
          Firm Details
        </Link>
        <Typography color="text.primary">Settings</Typography>
      </Breadcrumbs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Client Firm Settings
        </Typography>

        <Box sx={{ mt: 3 }}>
          <FormControl fullWidth sx={{ mb: 3 }} disabled={!isPmpAdmin()}>
            <InputLabel>Category</InputLabel>
            <Select
              value={settings.category}
              label="Category"
              onChange={(e) => setSettings({ ...settings, category: e.target.value as 'prepaid' | 'postpaid' | 'trial' | 'suspended' })}
            >
              <MenuItem value="prepaid">Prepaid</MenuItem>
              <MenuItem value="postpaid">Postpaid</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }} disabled={!isPmpAdmin()}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={settings.currency}
              label="Currency"
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
            >
              {currencies.map((curr) => (
                <MenuItem key={curr} value={curr}>
                  {curr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isPmpAdmin() && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/firms/${firmId}`)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Settings'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Client-specific dispositions</Typography>
            <Typography variant="body2" color="text.secondary">
              These extra dispositions appear only on this client&apos;s cases, in addition to the global list from Timer Configuration.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openAddDisposition}>
            Add disposition
          </Button>
        </Box>

        {dispositionsLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dispositions.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.id}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.is_active ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEditDisposition(d)} aria-label="Edit">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteDisposition(d.id)} aria-label="Deactivate" color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {dispositions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No client-specific dispositions. Add one to make it available only for this firm.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dispositionDialogOpen} onClose={() => setDispositionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dispositionEditId != null ? 'Edit Disposition' : 'Add Disposition'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={dispositionName}
            onChange={(e) => setDispositionName(e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={dispositionIsActive} onChange={(e) => setDispositionIsActive(e.target.checked)} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispositionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDisposition} variant="contained" disabled={dispositionSaving}>
            {dispositionSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientFirmSettings;
